// ENG-9125: refuse to spawn a session whose declared MCP bundles are not yet
// on disk in the host-shared `~/.augmented/_mcp` mount.
//
// Why this exists — the agt-aws-1 outage, 2026-08-18:
//
//   `~/.augmented/_mcp` is a SINGLE host-shared directory holding every MCP
//   bundle (index.js, slack-channel.js, direct-chat-channel.js, ...). It is
//   written once per manager by deployMcpAssets() and bind-mounted read-only
//   into every agent container. It is therefore a single point of failure for
//   every agent on the box.
//
//   That morning the containers started at 08:18 and the bundles did not land
//   until 08:43. Every agent booted against an EMPTY mount, so `node
//   /root/.augmented/_mcp/index.js` ENOENT'd and claude came up with zero MCP
//   tools bound. mcp-presence-reaper noticed and applied its only remedy —
//   tear down the tmux session so the manager respawns it. Restarting cannot
//   populate a mount, so it looped. Because EVERY stdio server was missing at
//   once the restart reason resolved to `mcp-presence-reaper`, which counts on
//   the breaker's CRASH tally (max 2 / 10 min), so the third restart tripped
//   the breaker and the agent was paused server-side. A crash-class trip is
//   never blind-auto-resumed (auto-resume.ts gates on `provisioning`), so it
//   stayed paused until a human intervened.
//
//   13 of 16 agents on the host were paused between 08:24 and 08:37, across
//   five teams and multiple orgs. A paused agent still looks online and still
//   reads inbound — it just answers nobody. One was mute for 35 minutes and
//   was found only because a human happened to DM it.
//
// The load-bearing assumption this module replaces is written down verbatim in
// packages/core/src/provisioning/frameworks/claudecode/index.ts:
//
//     // Always use the local path — the manager's deployMcpAssets()
//     // guarantees ~/.augmented/_mcp/index.js exists before any session starts.
//
// It is an assumption, not a guarantee, and nothing enforced it. This module
// enforces it, at the one place that matters: immediately before the spawn.
//
// Design notes, in order of how badly getting them wrong would hurt:
//
//   1. FAIL OPEN on anything we cannot read or parse. A gate that blocks a
//      spawn because it could not read a file would be strictly worse than the
//      bug it fixes — it would brick agents on a transient read error. An
//      unreadable/absent/malformed .mcp.json returns "nothing missing" here and
//      is left to the existing (deliberately tolerant) spawn path. Compare
//      persistent-session.ts, where every pre-spawn check is observational-only
//      precisely because "refusing to spawn on a missing AGT_HOST would brick a
//      running agent".
//
//   2. ONLY gate on bundles WE point into the shared mount. An entry is checked
//      only when its command is `node` AND its first arg resolves inside the
//      _mcp directory. npx entries, remote/HTTP servers, and anything pointing
//      outside the mount are ignored entirely — they have their own failure
//      modes and their own cold-start grace in the reaper. When `_mcp` is
//      populated (the normal case) this returns an empty array and behaviour is
//      completely unchanged.
//
//   3. NO side effects, NO clock, NO process spawning. Pure and synchronous, so
//      it costs nothing on the hot poll path and is trivially table-testable.
//
// Pure helpers exposed for tests:
//   • getSharedMcpDir()        — the host-shared _mcp path
//   • isPathInsideDir()        — containment check that resists prefix spoofing
//   • findMissingMcpBundles()  — the predicate; returns [] when all is well
//   • formatMissingMcpBundles()— stable one-line detail string for logs

import { existsSync as nodeExistsSync, readFileSync as nodeReadFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join, resolve, sep } from 'node:path';

/** A declared MCP server whose bundle is not on disk yet. */
export interface MissingMcpBundle {
  /** The key in `mcpServers`, e.g. "augmented" or "slack". */
  key: string;
  /** The absolute path we expected the bundle at. */
  path: string;
}

/**
 * Injection seams. Tests pass a fake `existsSync`/`readFileSync` (or a real
 * tmpdir — the house pattern) and an explicit `mcpDir` so they never depend on
 * the runner's actual home directory.
 */
export interface McpAssetsReadinessDeps {
  /** Defaults to `~/.augmented/_mcp`. */
  mcpDir?: string;
  existsSync?: (path: string) => boolean;
  readFileSync?: (path: string, encoding: 'utf-8') => string;
}

/**
 * The host-shared MCP bundle directory. Mirrors deployMcpAssets()'s target
 * (`manager-worker.ts`) and the docker bind-mount source (`persistent-session.ts`).
 */
export function getSharedMcpDir(homeDir: string = homedir()): string {
  return join(homeDir, '.augmented', '_mcp');
}

/**
 * True when `candidate` is inside `dir`.
 *
 * Compares against `dir + sep` rather than a bare `startsWith(dir)` so a
 * sibling directory whose name merely begins with the same characters
 * (`/root/.augmented/_mcp-backup/x.js`) is NOT treated as inside `_mcp`. Both
 * sides are resolved first so `..` segments cannot smuggle a path in or out.
 */
export function isPathInsideDir(candidate: string, dir: string): boolean {
  const resolvedDir = resolve(dir);
  const resolvedCandidate = resolve(candidate);
  if (resolvedCandidate === resolvedDir) return false;
  return resolvedCandidate.startsWith(resolvedDir.endsWith(sep) ? resolvedDir : resolvedDir + sep);
}

/**
 * Given the path to an agent's `.mcp.json`, return every declared server whose
 * `node`-launched bundle inside the shared `_mcp` mount is absent from disk.
 *
 * Returns an EMPTY array (i.e. "ready, go ahead and spawn") when:
 *   • the config is missing, unreadable, or not valid JSON  — fail open, see (1)
 *   • `mcpServers` is absent or not an object
 *   • no entry points into the shared mount
 *   • every such bundle exists
 *
 * Results are deduplicated by path and sorted by key, so the derived log line
 * is stable across polls and greps cleanly in manager.log.
 */
export function findMissingMcpBundles(
  mcpConfigPath: string,
  deps: McpAssetsReadinessDeps = {},
): MissingMcpBundle[] {
  const existsSync = deps.existsSync ?? nodeExistsSync;
  const readFileSync = deps.readFileSync ?? nodeReadFileSync;
  const mcpDir = deps.mcpDir ?? getSharedMcpDir();

  let parsed: unknown;
  try {
    parsed = JSON.parse(readFileSync(mcpConfigPath, 'utf-8'));
  } catch {
    // Fail open — an unreadable or malformed config is the existing spawn
    // path's problem, not this gate's. See design note (1).
    return [];
  }

  const servers = (parsed as { mcpServers?: unknown } | null)?.mcpServers;
  if (!servers || typeof servers !== 'object' || Array.isArray(servers)) return [];

  const missing: MissingMcpBundle[] = [];
  const seenPaths = new Set<string>();

  for (const [key, rawEntry] of Object.entries(servers as Record<string, unknown>)) {
    if (!rawEntry || typeof rawEntry !== 'object' || Array.isArray(rawEntry)) continue;
    const entry = rawEntry as { command?: unknown; args?: unknown };

    // Only `node <bundle>` entries are ours to gate on. npx / remote / http
    // servers are out of scope — see design note (2).
    if (entry.command !== 'node') continue;
    if (!Array.isArray(entry.args) || entry.args.length === 0) continue;

    const bundlePath = entry.args[0];
    if (typeof bundlePath !== 'string' || bundlePath.length === 0) continue;
    if (!isPathInsideDir(bundlePath, mcpDir)) continue;

    const resolvedBundlePath = resolve(bundlePath);
    if (seenPaths.has(resolvedBundlePath)) continue;

    let present: boolean;
    try {
      present = existsSync(bundlePath);
    } catch {
      // A throwing existsSync (EACCES on a parent, a racing unmount) is not
      // proof of absence. Fail open rather than park the agent.
      continue;
    }

    if (!present) {
      seenPaths.add(resolvedBundlePath);
      missing.push({ key, path: bundlePath });
    }
  }

  return missing.sort((a, b) => a.key.localeCompare(b.key));
}

/**
 * One-line, stable description of a missing-bundle set, for the manager log and
 * the `PersistentSessionResult.detail` field an operator reads in the console.
 * Names the shared mount explicitly because the actionable fact is "the host's
 * _mcp is not populated", not "this agent is broken".
 */
export function formatMissingMcpBundles(missing: MissingMcpBundle[]): string {
  if (missing.length === 0) return 'all declared MCP bundles present';
  const detail = missing.map((m) => `${m.key} (${m.path})`).join(', ');
  return `MCP bundle(s) not yet on disk in the host-shared _mcp mount: ${detail}`;
}
